import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { initializeFirebaseWeb, prepareFirebaseWebStorage } from './app/firebase/firebase-core';
import Swal from 'sweetalert2';

if (environment.production) {
  enableProdMode();
}

// Global SweetAlert2 patch: auto-dismiss simple notifications after 10 seconds
// This wraps the original Swal.fire so we don't have to change every call site.
// It won't add a timer for confirm-like dialogs (ones that include showCancelButton, input, etc.)
(() => {
  const originalFire = Swal.fire.bind(Swal as any);
  const defaultTimer = 10000; // 10 seconds
  (Swal as any).fire = (...args: any[]) => {
    try {
      if (args.length === 1 && typeof args[0] === 'string') {
        const opts = { title: args[0], timer: defaultTimer, timerProgressBar: true, showConfirmButton: false };
        return originalFire(opts);
      }
      if (args.length === 1 && typeof args[0] === 'object') {
        const opts = { ...args[0] } as any;
        const hasExplicitTimer = opts.timer !== undefined;
        const isConfirmLike = opts.showCancelButton || opts.input || opts.showDenyButton || opts.willOpen || opts.didOpen || opts.html;
        // If this is a toast or a non-confirm alert and no explicit timer provided, set default
        if (!hasExplicitTimer && !isConfirmLike) {
          opts.timer = defaultTimer;
          opts.timerProgressBar = true;
          opts.showConfirmButton = opts.showConfirmButton ?? false;
        }
        // If toast flag is present and no explicit timer, ensure timer is set
        if (opts.toast && !hasExplicitTimer) {
          opts.timer = opts.timer ?? defaultTimer;
          opts.timerProgressBar = opts.timerProgressBar ?? true;
        }
        return originalFire(opts);
      }
      if (args.length >= 2) {
        // Swal.fire(title, text) or Swal.fire(title, text, icon)
        const [title, text, icon] = args;
        const opts: any = {};
        if (title) opts.title = title;
        if (text) opts.text = text;
        if (icon) opts.icon = icon;
        opts.timer = opts.timer ?? defaultTimer;
        opts.timerProgressBar = true;
        opts.showConfirmButton = opts.showConfirmButton ?? false;
        return originalFire(opts);
      }
    } catch (e) {
      console.error('[Swal-wrapper] error while normalizing args, falling back to original', e);
      // Fall back to original behavior on error
      return originalFire(...args);
    }
    return originalFire(...args);
  };
  // Wrap Swal.mixin so mixins (often used for toasts) inherit the same auto-close behavior
  const originalMixin = Swal.mixin.bind(Swal as any);
  (Swal as any).mixin = (opts: any) => {
    const mixinInstance = originalMixin(opts);
    // Wrap the mixin's fire method
    const origMixinFire = mixinInstance.fire.bind(mixinInstance as any);
    (mixinInstance as any).fire = (...margs: any[]) => {
      try {
        // Normalize to single options object
        if (margs.length === 1 && typeof margs[0] === 'object') {
          const mo = { ...margs[0] };
          const hasExplicitTimer = mo.timer !== undefined;
          const isConfirmLike = mo.showCancelButton || mo.input || mo.showDenyButton || mo.willOpen || mo.didOpen || mo.html;
          if (!hasExplicitTimer && !isConfirmLike) {
            mo.timer = mo.timer ?? defaultTimer;
            mo.timerProgressBar = mo.timerProgressBar ?? true;
            mo.showConfirmButton = mo.showConfirmButton ?? false;
          }
          if (mo.toast && !hasExplicitTimer) {
            mo.timer = mo.timer ?? defaultTimer;
            mo.timerProgressBar = mo.timerProgressBar ?? true;
          }
          return origMixinFire(mo);
        }
        if (margs.length === 1 && typeof margs[0] === 'string') {
          const mo = { title: margs[0], timer: defaultTimer, timerProgressBar: true, showConfirmButton: false };
          return origMixinFire(mo);
        }
        if (margs.length >= 2) {
          const [title, text, icon] = margs;
          const optsLocal: any = {};
          if (title) optsLocal.title = title;
          if (text) optsLocal.text = text;
          if (icon) optsLocal.icon = icon;
          optsLocal.timer = optsLocal.timer ?? defaultTimer;
          optsLocal.timerProgressBar = optsLocal.timerProgressBar ?? true;
          optsLocal.showConfirmButton = optsLocal.showConfirmButton ?? false;
          return origMixinFire(optsLocal);
        }
      } catch (e) {
        console.error('[Swal-mixin-wrapper] error, falling back to original', e);
        return origMixinFire(...margs);
      }
      return origMixinFire(...margs);
    };
    return mixinInstance;
  };
})();

void prepareFirebaseWebStorage()
  .then(() => {
    initializeFirebaseWeb();
    return platformBrowserDynamic().bootstrapModule(AppModule);
  })
  .catch((err) => console.error(err));
